import React, { ReactNode, forwardRef, useCallback } from 'react';
import cx from 'classnames';
import { NeutralLink } from '@move-ui/link';
import { useCurrentUser } from '../../utils/currentUser';
import Divider from '../Divider';
import { MenuIcon } from '../Icon';
import styles from './MenuItem.css';
import { MaskedExternalLink } from '../Link';
import MenuItemLabel from './MenuItemLabel';
import { Conditions } from '../../types';

type Props = {
  badge?: 'messages' | 'notifications';
  className?: string;
  dataTestId?: string;
  icon?: string | MenuIcon | ReactNode;
  isMasked?: boolean;
  label: string;
  name: string;
  type?: 'action' | 'masked' | 'link' | 'route' | 'divider';
  url?: string;
  onClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

const MenuItem = forwardRef<HTMLAnchorElement, Props>(
  (
    {
      badge,
      className,
      dataTestId,
      icon,
      isMasked,
      label,
      name,
      type,
      url,
      onClick,
      ...rest
    },
    ref
  ): JSX.Element => {
    const {
      hasUnreadNotifications,
      hasUnreadMessages,
      username
    } = useCurrentUser();
    const handleClick = useCallback(
      (event) => onClick?.(name, { type, url }, event),
      [name, type, url, onClick]
    );

    if (type === 'divider') {
      return <Divider key={name} />;
    }

    const isBadgeVisible =
      (badge === 'messages' && hasUnreadMessages) ||
      (badge === 'notifications' && hasUnreadNotifications);

    if (type === 'masked' && url) {
      return (
        <MaskedExternalLink
          className={className}
          dataTestId={dataTestId}
          url={url}
          name={name}
          onClick={handleClick}
        >
          <div className={cx(styles.component)}>
            <MenuItemLabel
              icon={icon}
              isBadgeVisible={isBadgeVisible}
              text={label}
              username={username}
            />
          </div>
        </MaskedExternalLink>
      );
    }

    return (
      <NeutralLink
        className={cx(styles.component, className)}
        href={url}
        masked={isMasked}
        ref={ref}
        tabIndex={0}
        onClick={handleClick}
        data-testid={dataTestId}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      >
        <MenuItemLabel
          icon={icon}
          isBadgeVisible={isBadgeVisible}
          text={label}
          username={username}
        />
      </NeutralLink>
    );
  }
);

export default MenuItem;
